type MediaType =
  | "floorplan"
  | "photo"
  | "power profile"
  | "video"
  | "3dtour"
  | "photo overlay"
  | "advertisement"
  | "photo overlay with gallery";

import { debugLog } from "@rea-argonaut/analytics";

export type ClickThroughSourceMediaType = {
  url?: string;
  index: number;
  type: MediaType;
};
export interface ClickThroughSource {
  page?: string;
  element?: string;
  source_type?: string;
  media?: ClickThroughSourceMediaType;
  additional_field_key?: string;
  additional_field_value?: string;
}

export interface ClickThroughSourceLocalStorageValue
  extends ClickThroughSource {
  pathname: string;
}

export const CTS_LOCAL_STORAGE_KEY = "argonaut_realestate_cts";

export const getClickThroughSource = ({
  page,
  element,
  sourceType,
  media,
  additionalFieldKey,
  additionalFieldValue,
}: {
  page?: string;
  element?: string;
  sourceType?: string;
  media?: ClickThroughSourceMediaType;
  additionalFieldKey?: string;
  additionalFieldValue?: string;
} = {}): ClickThroughSource | undefined => {
  debugLog("Getting CTS values.");
  if (
    page ||
    element ||
    sourceType ||
    media ||
    additionalFieldKey ||
    additionalFieldValue
  ) {
    const clickThroughSource = {
      page,
      element,

      source_type: sourceType,

      media,
      additional_field_key: additionalFieldKey,
      additional_field_value: additionalFieldValue,
    };
    debugLog(
      "Received CTS values from getPageData arguments. ",
      clickThroughSource,
    );
    return clickThroughSource;
  }
  return (
    getClickThroughSourceFromLocalStorage(window.localStorage) ??
    getClickThroughSourceFromParamsString(window.location.search)
  );
};

export const getClickThroughSourceFromLocalStorage = (
  localStorage?: typeof window.localStorage,
): undefined | ClickThroughSource => {
  if (!localStorage) {
    debugLog(
      "Attempted to get CTS values from local storage but local storage is not available.",
    );

    return undefined;
  }
  try {
    const storedValue = localStorage.getItem(CTS_LOCAL_STORAGE_KEY);
    if (!storedValue) {
      debugLog(
        "Attempted to get CTS values from local storage, but none found.",
      );

      return undefined;
    }

    const decodedValue = JSON.parse(storedValue);
    if (!isClickThroughSourceLocalStorageValue(decodedValue)) {
      debugLog(
        "Attempted to get CTS values from local storage, but local storage value was not valid.",
      );

      return undefined;
    }

    const { pathname, ...clickThroughTracking } = decodedValue;
    const targetPagePath = pathname.startsWith("/") ? pathname : `/${pathname}`;

    if (window.location.pathname !== targetPagePath) {
      debugLog(
        `Destination pathname does not match CTS pathname stored in local storage, params pathname is ${window.location.pathname}, target page path in local storage is ${targetPagePath}.`,
      );

      return undefined;
    }

    debugLog("Received CTS values from LocalStorage.", clickThroughTracking);

    return clickThroughTracking;
  } catch (_) {
    debugLog("Error when attempting to retrieve Click Through Source.");
    return undefined;
  }
};

export type setClickThroughSourceToLocalStorageArgs = {
  clickThroughTargetPathname: string;
  clickThroughSourcePage?: string;
  clickThroughSourceElement?: string;
  clickThroughSourceType?: string;
  clickThroughSourceMedia?: ClickThroughSourceMediaType;
  clickThroughSourceAdditionalFieldKey?: string;
  clickThroughSourceAdditionalFieldValue?: string;
};

export const setClickThroughSourceToLocalStorage = (
  data: Readonly<setClickThroughSourceToLocalStorageArgs>,
): void => {
  try {
    if (!window?.localStorage || !data.clickThroughTargetPathname) {
      return;
    }
    const {
      clickThroughTargetPathname,
      clickThroughSourcePage,
      clickThroughSourceElement,
      clickThroughSourceType,
      clickThroughSourceMedia,
      clickThroughSourceAdditionalFieldKey,
      clickThroughSourceAdditionalFieldValue,
    } = data;

    const encodedValue = JSON.stringify({
      pathname: clickThroughTargetPathname,
      page: clickThroughSourcePage,
      element: clickThroughSourceElement,
      source_type: clickThroughSourceType,
      media: clickThroughSourceMedia,
      additional_field_key: clickThroughSourceAdditionalFieldKey,
      additional_field_value: clickThroughSourceAdditionalFieldValue,
    });
    localStorage.setItem(CTS_LOCAL_STORAGE_KEY, encodedValue);

    debugLog("CTS values is set to local storage.", {
      CTS_LOCAL_STORAGE_KEY: CTS_LOCAL_STORAGE_KEY,
      encodedValue,
    });
  } catch (_) {
    debugLog("Error when setting CTS values to local storage.");
  }
};

export const getClickThroughSourceFromParamsString = (
  paramsString: string,
): ClickThroughSource | undefined => {
  const searchParams = new URLSearchParams(paramsString);
  const page = searchParams.get("sourcePage") || undefined;
  const element = searchParams.get("sourceElement") || undefined;
  const sourceType = searchParams.get("sourceType") || undefined;
  const additionalFieldKey =
    searchParams.get("additionalFieldKey") || undefined;
  const additionalFieldValue =
    searchParams.get("additionalFieldValue") || undefined;

  if (
    page ||
    element ||
    sourceType ||
    additionalFieldKey ||
    additionalFieldValue
  ) {
    const clickThroughSourceFromParamsString = {
      page,
      element,
      source_type: sourceType,
      additional_field_key: additionalFieldKey,
      additional_field_value: additionalFieldValue,
    };

    debugLog(
      "Received CTS values from params string.",
      clickThroughSourceFromParamsString,
    );

    return clickThroughSourceFromParamsString;
  } else {
    debugLog(
      "Attempted to retrieve CTS values from params string, but no page, element or sourceType found in params string.",
    );

    return undefined;
  }
};

/**
 * @see https://github.com/sindresorhus/is-plain-obj/blob/main/index.js
 */
const isPlainObject = (
  value: unknown,
): value is Record<string | number | symbol, unknown> => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return (
    (prototype === null ||
      prototype === Object.prototype ||
      Object.getPrototypeOf(prototype) === null) &&
    !(Symbol.toStringTag in value) &&
    !(Symbol.iterator in value)
  );
};
const isString = (value: unknown): value is string => typeof value === "string";
const isObject = (value: unknown): value is object => typeof value === "object";

const isClickThroughSourceLocalStorageValue = (
  decodedJson: unknown,
): decodedJson is ClickThroughSourceLocalStorageValue => {
  if (!isPlainObject(decodedJson)) {
    return false;
  }
  const {
    pathname,
    page,
    element,
    source_type,
    media,
    additional_field_key,
    additional_field_value,
  } = decodedJson;
  if (!isString(pathname) || pathname.length === 0) {
    return false;
  }
  return (
    isString(page) ||
    isString(element) ||
    isString(source_type) ||
    isObject(media) ||
    isString(additional_field_key) ||
    isString(additional_field_value)
  );
};
